import { EMPTY_VALUE } from "./display";

/**
 * Coding task type (`codings.task_type` in AIA): what the coder must produce.
 *
 * Distinct from the coding METHOD (`aia_codage_type`, shown as "Type"), which is how the
 * coding is carried out — Live, Individual, Collective, ...
 */
export type ContractFamily = "individual" | "collective" | "collective_live";

/**
 * Contract family of a coding method (`aia_codage_type`). Mirrors `getContractFamily` in
 * the backend's `utils/codingTypes.ts` — keep the two in sync.
 *
 * `Individual_v2` bills as an individual coding: it splits a game into four codings
 * instead of two, but each is still contracted individually.
 *
 * `Collective` and `Software Collective Simple` return null — no contract template exists
 * for them.
 */
export const getContractFamily = (
  codageType: string | null | undefined,
): ContractFamily | null => {
  switch (codageType) {
    case "Individual":
    case "Individual_v2":
      return "individual";
    case "Software Collective Advanced":
      return "collective";
    // "live_coding" is a legacy value still present in older rows.
    // Keep the comment above the group: between two case labels it makes
    // eslint's no-fallthrough treat "Live" as a non-empty case.
    case "Live":
    case "live_coding":
      return "collective_live";
    default:
      return null;
  }
};

export const formatTaskType = (taskType: string | null | undefined): string => {
  switch (taskType) {
    case "events_coding":
      return "Détection";
    case "events_labeling":
      return "Annotation";
    case "full":
      return "Complet";
    default:
      return EMPTY_VALUE;
  }
};

/**
 * Coding method (`aia_codage_type`), for display.
 *
 * An unrecognised value is returned unchanged rather than blanked: a method AIA adds
 * should be visible as-is until it is translated here, not silently disappear from the
 * table. Same choice as `formatLanguage`.
 */
export const formatCodingType = (
  codageType: string | null | undefined,
): string => {
  switch (codageType) {
    case "Individual":
      return "Individuel";
    case "Individual_v2":
      return "Individuel v2";
    // "live_coding" is a legacy value still present in older rows.
    case "Live":
    case "live_coding":
      return "Live";
    case "Software Collective Advanced":
      return "Collectif avancé";
    // Neither of these appears as a coding method on the Codages page, but both exist
    // on games — which is what the Supervisions page reads, a supervision covering a
    // whole game. Untranslated they would show English in a French table.
    case "Software Collective Simple":
      return "Collectif simple";
    case "Collective":
      return "Collectif";
    default:
      return codageType || EMPTY_VALUE;
  }
};

/**
 * A coding's method and its task as one label — "Individuel v2 - Détection".
 *
 * The task half is dropped rather than shown as a dash when the row has none: the
 * column predates `aia_codage_task_type`, so older codings carry no task, and a
 * separator introducing an em dash reads as missing data rather than as absence.
 */
export const formatCodingTypeAndTask = (
  codageType: string | null | undefined,
  taskType: string | null | undefined,
): string => {
  const method = formatCodingType(codageType);
  const task = formatTaskType(taskType);

  return task === EMPTY_VALUE ? method : `${method} - ${task}`;
};

/**
 * Coding status as AIA reports it (`metadata.external_status`), for display.
 *
 * Unrecognised values are returned unchanged, for the same reason as above. The stored
 * value still drives the CSS class, so the colours are unaffected.
 */
export const formatCodingStatus = (
  status: string | null | undefined,
): string => {
  switch (status) {
    case "To plan":
      return "À faire";
    case "In progress":
      return "En cours";
    case "Done":
      return "Terminé";
    default:
      return status || EMPTY_VALUE;
  }
};
